import React, { useState } from "react";

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="text-slate-600">{label}</div>
      <div className="font-semibold text-slate-900 text-right break-words max-w-[60ch]">
        {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value ?? "—")}
      </div>
    </div>
  );
}

export default function StepReview({
  borrowerType,
  productType,
  productId,
  formData,
  fullPayload,
  onBack,
  onConfirm,
}) {
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);

  // Extract property address from multiple possible locations
  let propertyAddress = "";
  if (formData) {
    propertyAddress = formData.propertyAddress || formData.property_address || "";
  }
  
  // Also check fullPayload if available
  if (!propertyAddress && fullPayload) {
    const btlData = fullPayload.btl || fullPayload.payloadObj?.btl || {};
    if (borrowerType === "individual" || borrowerType === "solo") {
      propertyAddress = btlData.solo?.propertyAddress || "";
    } else if (borrowerType === "limited") {
      propertyAddress = btlData.limited?.propertyAddress || "";
    } else if (borrowerType === "partnership") {
      propertyAddress = btlData.partnership?.propertyAddress || "";
    } else if (borrowerType === "pension") {
      propertyAddress = btlData.pension?.propertyAddress || "";
    }
    
    // Fallback to formData in fullPayload
    if (!propertyAddress) {
      const payloadFormData = fullPayload.formData || fullPayload.payloadObj?.formData || {};
      propertyAddress = payloadFormData.propertyAddress || payloadFormData.property_address || "";
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-sky-50 border border-sky-200 p-5">
        <h2 className="text-lg font-bold text-sky-900">Review your application</h2>
        <p className="text-sm text-sky-800 mt-1">
          Check everything looks correct before submitting.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 p-5 space-y-3">
        <Row
          label="Product Type"
          value={
            String(productType || "").toUpperCase() === "CBTL"
              ? "Complex BTL"
              : productType
          }
        />
        {productId && <Row label="Product ID" value={productId} />}
        <Row label="Borrower Type" value={borrowerType} />
      </div>

      {propertyAddress && (
        <div className="rounded-2xl border border-slate-200 p-5 space-y-3">
          <div className="text-base font-semibold text-slate-800 mb-2">Property Information</div>
          <Row label="Security Address" value={propertyAddress} />
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 p-5 space-y-3">
        <div className="text-base font-semibold text-slate-800 mb-2">Form details</div>
        {/* Render key/value pairs from formData */}
        {formData ? (
          Object.entries(formData)
            .filter(([k]) => k !== "propertyAddress" && k !== "property_address") // Don't duplicate property address
            .map(([k, v]) => (
              <Row key={k} label={k} value={v} />
            ))
        ) : (
          <div className="text-slate-500 text-sm">No form data found.</div>
        )}
      </div>

      <div className="space-y-4 pt-4 border-t border-slate-200">
        <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            className="mt-[3px]"
            checked={agree1}
            onChange={(e) => setAgree1(e.target.checked)}
          />
          I declare the information provided is true and complete.
        </label>

        <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            className="mt-[3px]"
            checked={agree2}
            onChange={(e) => setAgree2(e.target.checked)}
          />
          Your property may be repossessed if you do not keep up repayments on your mortgage.
        </label>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 rounded-xl bg-slate-200 hover:bg-slate-300"
        >
          ← Back
        </button>
        <button
          type="button"
          disabled={!agree1 || !agree2}
          onClick={onConfirm}
          className={`px-6 py-2 rounded-xl text-white ${agree1 && agree2 ? "bg-sky-500 hover:bg-sky-600" : "bg-slate-300 cursor-not-allowed"
            }`}
        >
          Submit application →
        </button>
      </div>
    </div>
  );
}
